import { Form, InputNumber } from 'antd';
import { FC } from 'react';
import { useIntl } from 'react-intl';

import { useAppSelector } from '@/shared/hooks';

import { authModel } from '@/entities/auth';
import { billingModel } from '@/entities/billing';

import {
  CUSTOM_PRICE_FIELD_NAME,
  i18n,
  MAX_CUSTOM_PRICE,
  STRIPE_MAX_CUSTOM_PRICE,
} from '../../config';
import { getInvoicePrice, normalizeCustomPrice } from '../../lib';

type CustomPriceFieldProps = {
  listPrice?: string;
  currency?: string;
  disabled?: boolean;
};

// Billing accepts a custom price only from admins with basic access.
export const CustomPriceField: FC<CustomPriceFieldProps> = (props) => {
  const { listPrice, currency, disabled } = props;

  const { formatMessage, formatNumber } = useIntl();

  const form = Form.useFormInstance();
  const customPrice = normalizeCustomPrice(
    Form.useWatch(CUSTOM_PRICE_FIELD_NAME, form),
  );
  const hasAdminBasicAccess = useAppSelector(
    authModel.selectors.selectHasAdminBasicAccess,
  );
  const { WithoutStripeInvoices } = useAppSelector(
    billingModel.selectors.selectAccountInfo,
  );
  const maxCustomPrice = WithoutStripeInvoices
    ? MAX_CUSTOM_PRICE
    : STRIPE_MAX_CUSTOM_PRICE;

  if (!hasAdminBasicAccess) {
    return null;
  }

  const formatPrice = (value: number) =>
    formatNumber(value, {
      style: 'currency',
      currency: currency || 'USD',
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

  return (
    <Form.Item
      name={CUSTOM_PRICE_FIELD_NAME}
      label={formatMessage(i18n.customPriceMessages.label)}
      extra={
        listPrice === undefined
          ? formatMessage(i18n.customPriceMessages.hint)
          : formatMessage(i18n.customPriceMessages.summary, {
              listPrice: formatPrice(Number(listPrice)),
              invoicePrice: formatPrice(
                getInvoicePrice(listPrice, customPrice),
              ),
            })
      }
      rules={[
        {
          type: 'integer',
          min: 0,
          max: maxCustomPrice,
          message: formatMessage(i18n.customPriceMessages.integerValidation, {
            max: formatNumber(maxCustomPrice),
          }),
        },
      ]}
    >
      {/* No min/max here: InputNumber would silently clamp, e.g. -5 into a free sale. */}
      <InputNumber
        step={1}
        style={{ width: '100%' }}
        addonAfter={currency}
        placeholder={listPrice}
        disabled={disabled}
      />
    </Form.Item>
  );
};
